import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { Dictionary, Me, Member, Priority, Queue, Status, TrackerContext, Tenant } from './types';

/** Кто я, какие контексты доступны и в какие трекеры я уже вхожу. */
export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/me') });

/**
 * Вход в контекст: сервер сам находит трекер этой роли или заводит его, если это первый вход.
 * Клиент не придумывает ни имени, ни идентификатора — он только называет роль.
 */
export function useEnterContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (context: TrackerContext) =>
      api.post<Tenant>('/tenants/enter', {
        roleType: context.roleType,
        companyId: context.companyId ?? null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
}

/**
 * Справочники тенанта одним хуком: статусы, типы, приоритеты и очереди нужны почти на каждом
 * экране, чтобы показывать названия вместо идентификаторов. Тянуть их порознь в каждом компоненте
 * значило бы четыре запроса на экран.
 */
export function useTenantCatalog(tenantId: string) {
  const statuses = useQuery({
    queryKey: ['statuses', tenantId],
    queryFn: () => api.get<Status[]>(`/tenants/${tenantId}/statuses`),
  });
  const types = useQuery({
    queryKey: ['types', tenantId],
    queryFn: () => api.get<Dictionary[]>(`/tenants/${tenantId}/issue-types`),
  });
  const priorities = useQuery({
    queryKey: ['priorities', tenantId],
    queryFn: () => api.get<Priority[]>(`/tenants/${tenantId}/priorities`),
  });
  const queues = useQuery({
    queryKey: ['queues', tenantId],
    queryFn: () => api.get<{ content: Queue[] }>(`/tenants/${tenantId}/queues?size=200`),
  });

  const byId = <T extends { id: string }>(rows: T[] | undefined) =>
    new Map((rows ?? []).map((row) => [row.id, row]));

  return {
    statuses: statuses.data ?? [],
    types: types.data ?? [],
    priorities: priorities.data ?? [],
    queues: queues.data?.content ?? [],
    statusById: byId(statuses.data),
    typeById: byId(types.data),
    priorityById: byId(priorities.data),
    queueById: byId(queues.data?.content),
    isLoading: statuses.isLoading || types.isLoading || queues.isLoading,
  };
}

export const useMembers = (tenantId: string) =>
  useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => api.get<{ content: Member[] }>(`/tenants/${tenantId}/members?size=200`),
  });